/**
 * SessionCardDecoration — wraps a session card with the admin's chosen
 * live-state visual treatment. Renders only when lifecycle.phase === 'live'.
 *
 * Styles:
 *   red_pulse        → pulsing red border + soft glow (default, high-attention)
 *   green_glow       → solid green border + radial glow (positive / sport vibe)
 *   gradient_banner  → animated gradient bar across the top of the card (premium)
 *   minimal          → subtle border + LIVE badge only (already on the badge)
 *
 * For non-live phases, also subtly tints the card border based on phase so
 * Scheduled vs Open vs Locked vs Ended is glanceable at the card-edge.
 */

const NON_LIVE_BORDER = {
  scheduled:    'border-slate-300 dark:border-slate-700',
  rsvp_open:    'border-emerald-500/40',
  locked:       'border-sky-500/40',
  needs_guests: 'border-amber-500/50',
  ended:        'border-amber-500/40',
  completed:    'border-border opacity-70',
  cancelled:    'border-rose-500/30',
};

function liveWrapperClass(style) {
  switch (style) {
    case 'green_glow':
      return 'border-2 border-emerald-500 shadow-[0_0_24px_-2px_rgba(16,185,129,0.55)]';
    case 'gradient_banner':
      // Border stays subtle; the animated bar at the top is the hero treatment.
      return 'border-2 border-amber-400/40 shadow-lg';
    case 'minimal':
      return 'border-2 border-red-500/50';
    case 'red_pulse':
    default:
      return 'border-2 border-red-500 animate-pulse shadow-[0_0_24px_-2px_rgba(239,68,68,0.6)]';
  }
}

export default function SessionCardDecoration({ lifecycle, style = 'red_pulse', children }) {
  const phase = lifecycle?.phase || 'scheduled';
  const isLive = phase === 'live';
  const wrapperCls = isLive ? liveWrapperClass(style) : (NON_LIVE_BORDER[phase] || NON_LIVE_BORDER.scheduled);
  return (
    <div
      data-testid={`session-card-deco-${phase}${isLive ? `-${style}` : ''}`}
      className={`relative rounded-2xl ${wrapperCls} transition-all`}
    >
      {/* Animated gradient banner sits inside the rounded container at the top */}
      {isLive && style === 'gradient_banner' && (
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-1.5 rounded-t-2xl overflow-hidden"
          data-testid="live-gradient-banner"
        >
          <div
            className="h-full w-[200%] -ml-[50%] bg-gradient-to-r from-red-500 via-amber-400 to-emerald-500"
            style={{ animation: 'splitease-live-bar 2.4s linear infinite' }}
          />
        </div>
      )}
      {children}
      <style>{`@keyframes splitease-live-bar { 0% { transform: translateX(0); } 100% { transform: translateX(50%); } }`}</style>
    </div>
  );
}
